import { useCallback } from 'react';

import { sendClick } from 'reaxl-analytics';
import { psxVDPVHRClick } from 'reaxl-analytics-handlers';

import getPsxVendor from '@/utilities/getPsxVendor';
import { isEmpty } from '@/utilities/objectUtils';

import { inventoryProductClick } from '@/analytics/inventoryAnalyticsHandlers';

// VHR link labels mapping
const vhrLinkLabelsMap = {
    CARFAX_APPENDED: 'carfaxAppended',
    EXPERIAN_APPENDED: 'experianAppended',
};

const vhrUpdateLinkLabels = {
    EXPERIAN_RETAIL: 'Get a Vehicle History Report',
    CARFAX_RETAIL: 'Get a CARFAX History Report',
    EXPERIAN_APPENDED: 'View the Free Vehicle History Report',
    CARFAX_APPENDED: 'View the Free CARFAX Report',
};

/**
 * Custom hook for handling VHR (Vehicle History Report) data and interactions
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.vhrTile - VHR tile data containing tileType, epn, link, etc.
 * @param {string} options.listingId - The vehicle listing ID
 * @param {boolean} [options.isPsx=false] - Whether this is a PSX context
 * @param {Function} [options.onEmailCtaClick] - Email CTA click handler for lead gate
 * @param {boolean} [options.removeVhrCarfaxTile=false] - Whether to remove CARFAX tiles
 *
 * @returns {Object} VHR data and handlers
 */
export default function useVhrData({
    vhrTile = {},
    listingId,
    isPsx = false,
    onEmailCtaClick,
    removeVhrCarfaxTile = false,
}) {
    const { tileType, epn, link: vhrTileLink } = vhrTile;

    const linkText = vhrUpdateLinkLabels[tileType];
    const labelKey = vhrLinkLabelsMap[tileType];
    const vhrLink = vhrTileLink?.href;
    const isCarfaxTile = tileType?.split('_')[0] === 'CARFAX';
    const isExperianTile = tileType?.split('_')[0] === 'EXPERIAN';
    const isLeadGateEnabled = typeof onEmailCtaClick === 'function' && !!labelKey;

    /* We only want to remove the tile/link if it's a Carfax tile AND the Optimizely flag is turned on for VDP */
    /* We always want to show Experian tiles. We should also show Carfax tiles if the experiment is off */
    const showTile = !((removeVhrCarfaxTile && isCarfaxTile) || isEmpty(vhrTile));

    // Click handler
    const handleClick = useCallback((event) => {
        const type = isPsx ? psxVDPVHRClick : inventoryProductClick;
        const data = isPsx ? {
            listingId,
            vendor: getPsxVendor(tileType),
            epn,
        } : {
            epn,
            tileType,
        };

        sendClick(type, event, data);

        if (isCarfaxTile && removeVhrCarfaxTile) window.open(atob(vhrLink), '_blank');
    }, [isPsx, isCarfaxTile, removeVhrCarfaxTile, listingId, tileType, epn, vhrLink]);

    return {
        tileType,
        epn,
        vhrLink,
        vhrTileLink,
        linkText,
        isCarfaxTile,
        isExperianTile,
        isLeadGateEnabled,
        showTile,
        labelKey,
        handleClick,
    };
}
